/**
 * 权限读取工具（新模型）。两根轴：
 *  - 系统级身份 localStorage.userType：'user' | 'admin' | 'mh_member' | 'mh_partner'（admin 默认全权限）
 *  - 权限配置 localStorage.availableItem：JSON 字符串，四个命名空间 RENDER/CASE/PRODUCTS/USER_MANAGEMENT
 *
 * 案件角色存在 available_item.CASE.role（SUBMITTER/DOER/ADMIN）；平台 admin 视同案件 ADMIN。
 * 设计见 doc/account-permission-system-redesign.md。
 */

/** @typedef {'user'|'admin'|'mh_member'|'mh_partner'} SystemRole */
/** @typedef {'SUBMITTER'|'DOER'|'ADMIN'|'VIEWER'} CaseRole 旧モデル。移行前データの読み替え用 */
/** @typedef {'GENERAL'|'RENDER_USER'|'BS_MEMBER'|'MH_PARTNER'|'MH_MEMBER'|'PLATFORM_ADMIN'} UserRoleKey */

/** 系统级身份 */
export const SYSTEM_USER = 'user'
export const SYSTEM_ADMIN = 'admin'
export const SYSTEM_MH_MEMBER = 'mh_member'
export const SYSTEM_MH_PARTNER = 'mh_partner'

/** 与 user 权限相同的普通系统用户类型（仅标签不同） */
export const REGULAR_SYSTEM_USER_TYPES = [SYSTEM_USER, SYSTEM_MH_MEMBER, SYSTEM_MH_PARTNER]

/** 全部系统身份（含 admin） */
export const ALL_SYSTEM_USER_TYPES = [...REGULAR_SYSTEM_USER_TYPES, SYSTEM_ADMIN]

/** 案件角色（available_item.CASE.role 的取值） */
export const CASE_ROLE_SUBMITTER = 'SUBMITTER'
export const CASE_ROLE_DOER = 'DOER'
export const CASE_ROLE_ADMIN = 'ADMIN'
/** 閲覧者（MH社員）：全案件を閲覧のみ。操作・ファイル変更は不可（チャットは可）。MHパートナーは 2026-10 から担当する案件だけ */
export const CASE_ROLE_VIEWER = 'VIEWER'

/**
 * ロール（6段階の階梯）。available_item.ROLE に入る。数字が大きいほど上位。
 * 案件依頼 / 案件担当 / 案件管理 / ユーザー管理 はロールとは独立した追加能力。
 */
export const ROLE_GENERAL = 'GENERAL'
export const ROLE_RENDER_USER = 'RENDER_USER'
export const ROLE_BS_MEMBER = 'BS_MEMBER'
export const ROLE_MH_PARTNER = 'MH_PARTNER'
export const ROLE_MH_MEMBER = 'MH_MEMBER'
export const ROLE_PLATFORM_ADMIN = 'PLATFORM_ADMIN'

/** ロールキー → 階層。表示順もこの順 */
export const USER_ROLE_LEVELS = {
  [ROLE_GENERAL]: 1,
  [ROLE_RENDER_USER]: 2,
  [ROLE_BS_MEMBER]: 3,
  [ROLE_MH_PARTNER]: 4,
  [ROLE_MH_MEMBER]: 5,
  [ROLE_PLATFORM_ADMIN]: 6,
}

/** ロールキー一覧（階層の昇順） */
export const USER_ROLES = [
  ROLE_GENERAL,
  ROLE_RENDER_USER,
  ROLE_BS_MEMBER,
  ROLE_MH_PARTNER,
  ROLE_MH_MEMBER,
  ROLE_PLATFORM_ADMIN,
]

/** ロールキーの階層を返す。未知は 0 */
export function roleLevelOf(role) {
  return USER_ROLE_LEVELS[String(role || '').toUpperCase()] || 0
}

/** Lv5 以上は全商品固定（商品の個別指定を受け付けない）。MHパートナー（Lv4）は 2026-10 から個別指定 */
export function roleUsesAllProducts(role) {
  return roleLevelOf(role) >= USER_ROLE_LEVELS[ROLE_MH_MEMBER]
}

/** ロール一覧（後端 UserRole と同じキー）。ドロップダウンの表示順もこの順 */
export const PERMISSION_PRESETS = USER_ROLES

/**
 * 兼容旧的"协作类型"小写词表（user/doer/admin），供 CaseProject.vue / CaseForm.vue /
 * ProjectSidebar.vue / 案件能力表等既有消费方继续使用。现在从 available_item.CASE.role 反推，
 * 没有案件角色时返回 null（不再默认 user）。
 */
export const COLLAB_USER = 'user'
export const COLLAB_DOER = 'doer'
export const COLLAB_ADMIN = 'admin'
export const COLLAB_VIEWER = 'viewer'

/** 解析 localStorage.availableItem（JSON 字符串）为对象；失败兜底返回 {}。 */
export function readStoredAvailableItem() {
  try {
    const parsed = JSON.parse(localStorage.getItem('availableItem') || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    return {}
  }
}

/** 系统级身份：'user' | 'admin' | 'mh_member' | 'mh_partner' | null（未登录/未知）。 */
export function readStoredSystemRole() {
  try {
    const v = localStorage.getItem('userType')
    return v && String(v).trim() ? String(v).trim() : null
  } catch (e) {
    return null
  }
}

/** 是否普通系统用户（与 user 权限相同，含 mh_member / mh_partner）。 */
export function isRegularSystemUser(userType) {
  if (!userType) return false
  return REGULAR_SYSTEM_USER_TYPES.includes(String(userType).trim())
}

/** 是否 MH社員（user_type=mh_member）。MHパートナーとは区別する。 */
export function readStoredIsMhMember() {
  return readStoredSystemRole() === SYSTEM_MH_MEMBER
}

/**
 * 案件チャットを使えるか。閲覧者（VIEWER）は原則不可だが、MH社員だけは全案件のチャットに参加できる。
 * 案件ロールを持たないアカウントは案件自体に入れないので false。
 */
export function canUseCaseChat() {
  if (isAdmin()) return true
  if (readStoredCanManageCases() || readStoredCanDoCase() || readStoredCanRequestCase()) return true
  // 閲覧だけのロールは Lv5（MH社員）以上のみチャットに入れる
  return roleLevelOf(readStoredUserRole()) >= USER_ROLE_LEVELS[ROLE_MH_MEMBER]
}

/** 是否平台管理员（user_type=admin，默认全权限）。 */
export function isAdmin() {
  return readStoredSystemRole() === SYSTEM_ADMIN
}

/**
 * 能否进入渲染后台 /admin/*、/back/**：
 * user_type=admin，或 available_item 中 RENDER.manageRender===true（含遗留 manageAccounts）。
 */
export function canAccessRenderAdminPage() {
  return readStoredCanManageRender()
}

/**
 * 当前账号案件角色：admin 视同 ADMIN；否则读 available_item.CASE.role；无角色返回 null。
 * @returns {CaseRole|null}
 */
export function readStoredCaseRole() {
  if (isAdmin()) return CASE_ROLE_ADMIN
  const ai = readStoredAvailableItem()
  const role = ai && ai.CASE ? ai.CASE.role : null
  return role || null
}

/** 案件システムに入れるか（依頼 / 担当 / 管理 / 全案件閲覧 のどれかを持つ）。 */
export function readStoredHasCaseRole() {
  if (isAdmin()) return true
  return readStoredCanViewAllCases() || readStoredCanRequestCase() || readStoredCanDoCase()
}

/**
 * 兼容旧词表：案件詳細の添付ケイパビリティ判定に使う。
 * 案件管理 → admin、案件担当 → doer、案件依頼 → user、閲覧だけ → viewer、権限なし → null。
 * @returns {'user'|'doer'|'admin'|'viewer'|null}
 */
export function readStoredCollaborationType() {
  if (isAdmin() || readStoredCanManageCases()) return COLLAB_ADMIN
  if (readStoredCanDoCase()) return COLLAB_DOER
  if (readStoredCanRequestCase()) return COLLAB_USER
  if (readStoredCanViewAllCases()) return COLLAB_VIEWER
  return null
}

/** 現在のロール。available_item.ROLE を優先し、無ければ旧 user_type / CASE.role から推定する */
export function readStoredUserRole() {
  if (isAdmin()) return ROLE_PLATFORM_ADMIN
  const ai = readStoredAvailableItem()
  const stored = String((ai && ai.ROLE) || '').toUpperCase()
  if (stored && stored !== ROLE_PLATFORM_ADMIN && USER_ROLE_LEVELS[stored]) return stored
  const legacy = ai && ai.CASE ? ai.CASE.role : null
  const systemRole = readStoredSystemRole()
  if (legacy === CASE_ROLE_ADMIN || legacy === CASE_ROLE_DOER || legacy === CASE_ROLE_VIEWER) {
    return systemRole === SYSTEM_MH_PARTNER ? ROLE_MH_PARTNER : ROLE_MH_MEMBER
  }
  if (legacy === CASE_ROLE_SUBMITTER) return ROLE_BS_MEMBER
  if (systemRole === SYSTEM_MH_PARTNER) return ROLE_MH_PARTNER
  if (systemRole === SYSTEM_MH_MEMBER) return ROLE_MH_MEMBER
  return readStoredRenderAccess() ? ROLE_RENDER_USER : ROLE_GENERAL
}

/** Employees and platform administrators can submit their own project evaluations. */
export function readStoredCanEvaluateIkkodate() {
  return [ROLE_MH_MEMBER, ROLE_PLATFORM_ADMIN].includes(readStoredUserRole())
}

export function readStoredCanManageIkkodateEvaluations() {
  return readStoredUserRole() === ROLE_PLATFORM_ADMIN
}

export function readStoredCanViewIkkodateEvaluations() {
  return readStoredCanEvaluateIkkodate()
}

/** 現在のロールの階層（1〜6） */
export function readStoredRoleLevel() {
  return roleLevelOf(readStoredUserRole())
}

/** 会員専用お問合せ（ヘッダー右上のボタン）を出す：BS加盟店以上 */
export function readStoredCanUseMemberInquiry() {
  return readStoredRoleLevel() >= USER_ROLE_LEVELS[ROLE_BS_MEMBER]
}

/** S-AI（工務店向け AI 受注支援）：MH社員以上（2026-10 から社内だけに限定、バックエンドの SaiAccess と同じ） */
export function readStoredCanUseSai() {
  return readStoredRoleLevel() >= USER_ROLE_LEVELS[ROLE_MH_MEMBER]
}

/** ヘッダーに出す現在のアカウント名（ログイン時に保存した username） */
export function readStoredAccountName() {
  try {
    return String(localStorage.getItem('username') || '').trim()
  } catch (e) {
    return ''
  }
}

function caseFlag(key, legacyRole) {
  const ai = readStoredAvailableItem()
  const v = ai && ai.CASE ? ai.CASE[key] : undefined
  if (typeof v === 'boolean') return v
  return (ai && ai.CASE ? ai.CASE.role : null) === legacyRole
}

/** 案件を依頼できる（CASE.request）。案件管理も含む */
export function readStoredCanRequestCase() {
  if (isAdmin()) return true
  return caseFlag('request', CASE_ROLE_SUBMITTER) || readStoredCanManageCases()
}

/** 案件担当（CASE.doer）。担当者候補に載り、指名された案件を操作できる */
export function readStoredCanDoCase() {
  if (isAdmin()) return true
  return caseFlag('doer', CASE_ROLE_DOER)
}

/** 案件管理（CASE.manage）。全案件の操作・見積・段階確定 */
export function readStoredCanManageCases() {
  if (isAdmin()) return true
  return caseFlag('manage', CASE_ROLE_ADMIN)
}

/**
 * 見積書の作成・修正：案件管理、または案件担当を持つ MH社員以上（バックエンドの AvailableItemPolicy.canIssueQuotation と同じ）。
 * MHパートナーは案件担当を持っていても不可。
 */
export function readStoredCanIssueQuotation() {
  if (readStoredCanManageCases()) return true
  return caseFlag('doer', CASE_ROLE_DOER) && readStoredRoleLevel() >= USER_ROLE_LEVELS[ROLE_MH_MEMBER]
}

/** ユーザー管理（USER_MANAGEMENT.manageUsers） */
export function readStoredCanManageUsers() {
  if (isAdmin()) return true
  const ai = readStoredAvailableItem()
  const v = ai && ai.USER_MANAGEMENT ? ai.USER_MANAGEMENT.manageUsers : undefined
  if (typeof v === 'boolean') return v
  return (ai && ai.CASE ? ai.CASE.role : null) === CASE_ROLE_ADMIN
}

/**
 * 統計を閲覧できる（Lv5 MH社員 以上のロール、または管理の能力をどれか1つでも持つ）。
 *
 * 案件管理だけに絞っていたが、ユーザー管理・商品管理・レンダー管理の担当者も
 * 数字を見る側なので、管理の能力を持つなら誰でも見られるようにした。
 * 口径は「管理」入口（readStoredCanEnterManagementPage）と同じ。
 */
export function readStoredCanViewStatistics() {
  if (isAdmin()) return true
  if (roleLevelOf(readStoredUserRole()) >= USER_ROLE_LEVELS[ROLE_MH_MEMBER]) return true
  return readStoredCanEnterManagementPage()
}

/** ロール（Lv5 以上）または案件管理で全案件を閲覧できる。MHパートナーは担当する案件だけ */
export function readStoredCanViewAllCases() {
  if (isAdmin()) return true
  return roleLevelOf(readStoredUserRole()) >= USER_ROLE_LEVELS[ROLE_MH_MEMBER] || readStoredCanManageCases()
}

/** 能否进入「管理」页面（/projects/case-management）：案件管理者、商品管理、渲染管理三者任一（平台 admin 恒 true） */
export function readStoredCanEnterManagementPage() {
  if (isAdmin()) return true
  if (readStoredCanManageCases()) return true
  if (readStoredCanManageUsers()) return true
  if (readStoredCanManageProducts()) return true
  return readStoredCanManageRender()
}

/**
 * 当前账号可见商品范围：admin 返回 ['*']；否则读 available_item.PRODUCTS.items；
 * 返回数组或 null（未配置）。判断"是否全部商品"统一用 products.includes('*')。
 * @returns {string[]|null}
 */
export function readStoredProducts() {
  if (isAdmin()) return ['*']
  const role = readStoredUserRole()
  if (roleUsesAllProducts(role)) return ['*']
  const ai = readStoredAvailableItem()
  const items = ai && ai.PRODUCTS ? ai.PRODUCTS.items : null
  if (!Array.isArray(items)) return null
  // MHパートナーは指定した商品だけ。以前の全商品固定の名残 ["*"] は「商品なし」（後端と同じ）
  if (role === ROLE_MH_PARTNER && items.includes('*')) return []
  return items
}

/** 能不能用渲染图系统（RENDER.access）。 */
export function readStoredRenderAccess() {
  if (isAdmin()) return true
  const ai = readStoredAvailableItem()
  return !!(ai && ai.RENDER && ai.RENDER.access === true)
}

/** 当前账号进入 /projects 时的默认落点：S-AI（MH社員以上），其次渲染，再次案件。 */
export function readStoredProjectsHomePath() {
  // S-AI を使える人（MH社員以上）は S-AI のホームから。AI real CG ユーザー・加盟店は使えないので今までどおり
  if (readStoredCanUseSai()) return '/projects/s-ai'
  const hasRender = readStoredRenderAccess()
  if (hasRender) return '/projects/render'
  if (readStoredHasCaseRole()) return '/projects/cases'
  // 新規ユーザー（AI real CG も案件も権限なし）：サブスクリプション開始ページへ
  return '/wallets'
}

/** 権限なしの新規ユーザーか（AI real CG 不可・案件ロールなし・admin でない） */
export function readStoredIsNewUser() {
  if (isAdmin()) return false
  return !readStoredRenderAccess() && !readStoredHasCaseRole()
}

/** 能不能管理其他账号的渲染域（RENDER.manageRender，含遗留 manageAccounts）。 */
export function readStoredCanManageRender() {
  if (isAdmin()) return true
  const ai = readStoredAvailableItem()
  if (ai && ai.RENDER && ai.RENDER.manageRender === true) return true
  if (ai && ai.USER_MANAGEMENT && ai.USER_MANAGEMENT.manageAccounts === true) return true
  return false
}

/** 能不能发行/修改其他账号（ユーザー管理能力）。 */
export function readStoredCanManageAccounts() {
  return readStoredCanManageUsers()
}

/**
 * 能不能管理其他账号的商品范围（PRODUCTS.items）。跟后端 canManageProducts 口径一致：
 * admin、case_admin（CASE.role=ADMIN 隐含）、或 PRODUCTS.manageProducts=true 三条路径任一即可。
 */
export function readStoredCanManageProducts() {
  if (isAdmin()) return true
  if (readStoredCanManageCases()) return true
  const ai = readStoredAvailableItem()
  return !!(ai && ai.PRODUCTS && ai.PRODUCTS.manageProducts === true)
}

/**
 * 構造計算だけを単独で依頼できるか（CASE.requestStructureCalc）。
 * これを持つ加盟店は案件作成の「依頼内容」で 構造計算 を選べる。
 */
export function readStoredCanRequestStructureCalc() {
  if (isAdmin()) return true
  const ai = readStoredAvailableItem()
  return !!(ai && ai.CASE && ai.CASE.requestStructureCalc === true)
}

const DOER_AND_ADMIN_CAPS = {
  canUploadCaseAttachments: false,
  canDeleteCaseAttachments: false,
  canUploadDeliverables: true,
  canDeleteDeliverables: true,
  canUploadChatAttachments: true,
  canDownload: true,
}

const SUBMITTER_CAPS = {
  canUploadCaseAttachments: false,
  canDeleteCaseAttachments: false,
  canUploadDeliverables: false,
  canDeleteDeliverables: false,
  canUploadChatAttachments: true,
  canDownload: true,
}

const VIEWER_CAPS = {
  canUploadCaseAttachments: false,
  canDeleteCaseAttachments: false,
  canUploadDeliverables: false,
  canDeleteDeliverables: false,
  canUploadChatAttachments: false,
  canDownload: true,
}
/** 各角色在案件详情页的附件/成果物能力（入参为旧词表 user/doer/admin/viewer，null 视同提交者）。 */
export function resolveCaseCollaborationCapabilities(userType) {
  if (userType === COLLAB_DOER || userType === COLLAB_ADMIN) {
    return { ...DOER_AND_ADMIN_CAPS }
  }
  if (userType === COLLAB_VIEWER) {
    // 閲覧のみのロールでも、チャットに入れる（MH社員）なら添付は送れる
    return { ...VIEWER_CAPS, canUploadChatAttachments: canUseCaseChat() }
  }
  return { ...SUBMITTER_CAPS }
}
